/**
 * Why two screens show two numbers (register P1).
 *
 * Chris, on the call: Emailed Intake showed twenty-two records, the Ingestion
 * Queue five and the deal list thirteen — "a spread duplicates alone do not
 * explain." His acceptance test is one number, reconciled, with any difference
 * stated ON SCREEN as a reason rather than left to be inferred.
 *
 * The server has computed that arithmetic for a while. It was rendered nowhere,
 * so the disagreeing numbers stayed on screen and the reasons stayed in an
 * endpoint nobody called — which, from a reviewer's chair, is the same as not
 * having them.
 *
 * This lives in `shared/` rather than `intake/` because the acceptance test
 * names three surfaces and only two of them are intake's. The deal list is the
 * screen Chris counted thirteen on, and it renders the same table from the same
 * payload — one component, so the two screens cannot drift into explaining the
 * same difference two different ways.
 *
 * Collapsed by default: the desk works from the lists, and opens this when a
 * number looks wrong. Open, it has to close arithmetically — every reason
 * carries its record count, and the two directions are labelled separately.
 */
import { Fragment, useState, type ReactNode } from 'react'
import { ChevronDown, ChevronRight, Minus, Plus } from 'lucide-react'

import type { IntakeSurface } from '@/services/ingestion'

export interface ReconciliationSurfacesProps {
  /** The single intake number every surface is reconciled against. */
  total: number
  surfaces: IntakeSurface[]
  /**
   * Element id for the disclosure region, and the `aria-controls` target.
   * Distinct per host: two instances mounted in one document — Intake in a
   * route that stays warm, the pipeline in another — would otherwise emit
   * duplicate ids and point every trigger at whichever rendered first.
   */
  domId?: string
  /** The disclosure trigger's label, so each host can name its own question. */
  label?: string
  /**
   * Rendered inline on the trigger's row, before it.
   *
   * The deal list states its own arithmetic in one line and opens this table
   * beneath. Passing that line in — rather than placing it beside the whole
   * component — keeps the trigger row compact while the table still starts at
   * the container's left edge and spans its full width. Sat outside, the table
   * began wherever the line ended and left a void to its left.
   */
  lead?: ReactNode
}

function Reasons({
  rows,
  tone,
}: {
  rows: { reason: string; records: number; detail: string }[]
  tone: 'fewer' | 'more'
}) {
  const Icon = tone === 'fewer' ? Minus : Plus
  const colour = tone === 'fewer' ? 'text-stone-500' : 'text-sky-300/70'
  return (
    <>
      {rows.map((r) => (
        <tr key={`${tone}-${r.reason}`} className="align-top">
          <td className="py-0.5 pl-4 pr-2">
            <span className={`inline-flex items-center gap-1 ${colour}`}>
              <Icon className="h-2.5 w-2.5 shrink-0" aria-hidden />
              {r.reason}
            </span>
          </td>
          <td className="py-0.5 pr-2 text-right tabular-nums text-stone-300">{r.records}</td>
          <td className="py-0.5 text-stone-500">{r.detail}</td>
        </tr>
      ))}
    </>
  )
}

export default function ReconciliationSurfaces({
  total,
  surfaces,
  domId = 'intake-reconciliation-surfaces',
  label = 'Why each screen shows a different number',
  lead,
}: ReconciliationSurfacesProps) {
  const [open, setOpen] = useState(false)
  if (!surfaces.length) return null

  const Chevron = open ? ChevronDown : ChevronRight

  return (
    <div className="flex-shrink-0">
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
        {lead}
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-controls={domId}
          className="flex items-center gap-1 text-[10px] text-stone-500 transition-colors hover:text-stone-300 focus-visible:outline focus-visible:outline-1 focus-visible:outline-amber-400"
        >
          <Chevron className="h-3 w-3" aria-hidden />
          {label}
        </button>
      </div>

      {open && (
        <div id={domId} className="mt-1.5 overflow-x-auto">
          <table className="w-full min-w-[520px] max-w-[880px] text-[10px]">
            <caption className="sr-only">
              Each surface, the number it displays, and every reason it differs from the
              {` ${total} `}
              records in intake
            </caption>
            <thead>
              <tr className="border-b border-stone-800 text-stone-500">
                <th scope="col" className="py-1 pr-2 text-left font-medium">
                  Surface
                </th>
                <th scope="col" className="py-1 pr-2 text-right font-medium">
                  Shows
                </th>
                <th scope="col" className="py-1 text-left font-medium">
                  What it counts
                </th>
              </tr>
            </thead>
            <tbody>
              {surfaces.map((s) => (
                <Fragment key={s.surface}>
                  <tr className="border-t border-stone-800/60 first:border-0">
                    <td className="py-1 pr-2 font-medium text-stone-200">{s.surface}</td>
                    <td className="py-1 pr-2 text-right tabular-nums text-stone-100">{s.count}</td>
                    <td className="py-1 text-stone-500">
                      {s.shows}
                      {typeof s.fromIntake === 'number' && (
                        <span className="text-stone-600">
                          {` — ${s.fromIntake} of them from intake`}
                        </span>
                      )}
                    </td>
                  </tr>
                  <Reasons rows={s.because} tone="fewer" />
                  <Reasons rows={s.adds ?? []} tone="more" />
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
